import React, { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { components, SingleValue } from 'react-select';
import useMeasureFilterFormContext from '../../utils/useMeasureFilterFormContext';
import {
  IRelationalOperator,
  RelationalOperatorKey,
} from '../../../../../../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../../../../../../redux/hooks';
import { ReactSelectMenu } from '../../../../../../Dropdown/StyledReactSelect';
import { BasicDropdownIndicator } from '../../../../../../Dropdown/StyledReactSelect/SelectUtils';
import { colors } from '../../../../../../../config/styles';

interface IProps {
  filterIndex: number;
}

const SingleMeasureFilterRelOp = ({ filterIndex }: IProps) => {
  const { control, setFocus } = useMeasureFilterFormContext();

  const { field, fieldState } = useController({
    name: `filters.${filterIndex}.relOpKey`,
    control,
  });

  const { value, onChange } = field;

  const defaultMeasureRelOps = useAppSelector(
    ({ builder }) => builder.defaultMeasureRelOps,
  );

  const handleChange = (newValue: SingleValue<IRelationalOperator>) => {
    if (!newValue) {
      return;
    }

    const relOpKey = newValue.key;

    onChange(relOpKey);

    if (relOpKey === RelationalOperatorKey.Between) {
      setFocus(`filters.${filterIndex}.fromValue`);
    } else {
      setFocus(`filters.${filterIndex}.value`);
    }
  };

  const selectedValue = useMemo(() => {
    if (!value) {
      return null;
    }

    const foundRelOp = defaultMeasureRelOps.find(
      (relOp) => relOp.key === value,
    );

    return foundRelOp || null;
  }, [defaultMeasureRelOps, value]);

  const hasError = Boolean(fieldState.error);

  return (
    <ReactSelectMenu
      ref={field.ref}
      isMulti={false}
      options={defaultMeasureRelOps}
      onChange={handleChange}
      getOptionLabel={(relOp) => relOp.name}
      getOptionValue={(relOp) => relOp.key}
      value={selectedValue}
      components={{
        DropdownIndicator: (props) => BasicDropdownIndicator({ props }),
        SingleValue: components.SingleValue,
      }}
      styles={{
        control: (base) => {
          const result = {
            ...base,
            minWidth: '100%',
            maxWidth: '100%',
          };

          if (hasError) {
            result.borderColor = colors.mainError;
          }

          return result;
        },
        valueContainer: (base) => ({
          ...base,
          paddingLeft: '8px',
        }),
        placeholder: (base) => ({
          ...base,
          margin: 0,
        }),
      }}
      placeholder={'Operator'}
      isSearchable={false}
      isClearable={false}
      openMenuOnFocus
      hideSelectedOptions
      menuPortalTarget={document.body}
    />
  );
};

export default SingleMeasureFilterRelOp;
